import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@/shared/lib/utils";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Label } from "@/shared/ui/components/ui/label";
import { Checkbox } from "@/shared/ui/components/custom/Checkbox";
import { RadioButton } from "@/shared/ui/components/custom/RadioButton";
import { SearchInput } from "@/shared/ui/components/custom/SearchInput";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectBaseProps
  extends Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    "onChange" | "style" | "value"
  > {
  options: MultiSelectOption[];
  placeholder?: string;
  error?: string;
  onChange?: (values: string[]) => void;
  value?: string[];
  disabled?: boolean;
  label?: string;
  labelIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  innerLabel?: string;
  hint?: string;
  onErrorClear?: () => void;
  id?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  darkModeOnOpen?: boolean;
  checkboxClassName?: string;
  checkboxVariant?: "default" | "advanced";
  selectionMode?: "multiple" | "single"; // "multiple" uses checkboxes, "single" uses radio buttons
  showCount?: boolean;
  contentClassName?: string; // Custom class for dropdown content
  viewportClassName?: string; // Custom class for viewport (options container)
  searchable?: boolean;
  searchPlaceholder?: string;
  /** When set, replaces default trigger label whenever `value` has at least one item. */
  formatDisplayText?: (ctx: {
    value: string[];
    placeholder: string;
    selectionMode: "multiple" | "single";
    selectedLabels: string[];
  }) => string;
}

export interface MultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<
    MultiSelectBaseProps,
    "name" | "value" | "onChange" | "error" | "open" | "onOpenChange"
  > {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string[];
  onChange?: (values: string[]) => void;
  error?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const MultiSelectBase = React.forwardRef<
  HTMLButtonElement,
  MultiSelectBaseProps
>(
  (
    {
      className,
      options = [],
      placeholder,
      error,
      onChange,
      value = [],
      disabled,
      label,
      labelIcon,
      leftIcon,
      innerLabel,
      hint,
      onErrorClear,
      id,
      open: controlledOpen,
      onOpenChange: controlledOnOpenChange,
      darkModeOnOpen = false,
      checkboxVariant = "default",
      selectionMode = "multiple",
      showCount = true,
      contentClassName,
      viewportClassName,
      checkboxClassName,
      searchable = false,
      searchPlaceholder,
      formatDisplayText,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation();
    const selectId = id || React.useId();
    const hintId = `${selectId}-hint`;
    const hasError = Boolean(error);
    const [internalOpen, setInternalOpen] = React.useState(false);

    const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setIsOpen = controlledOnOpenChange || setInternalOpen;

    const baseOptions = options?.filter((o) => o.value !== "") ?? [];

    const [searchTerm, setSearchTerm] = React.useState("");

    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchTerm.trim()) return baseOptions;
      const term = searchTerm.toLowerCase();
      return baseOptions.filter((opt) =>
        opt.label.toLowerCase().includes(term)
      );
    }, [baseOptions, searchable, searchTerm]);

    const hasOptions = filteredOptions.length > 0;

    const handleCheckboxChange = (optionValue: string, checked: boolean) => {
      if (hasError && onErrorClear) onErrorClear();

      const newValues = checked
        ? [...value, optionValue]
        : value.filter((v) => v !== optionValue);

      onChange?.(newValues);
    };

    const handleRadioChange = (optionValue: string, checked: boolean) => {
      if (hasError && onErrorClear) onErrorClear();
      const newValues = checked ? [optionValue] : [];
      onChange?.(newValues);
    };

    const getDisplayText = () => {
      if (value.length === 0) return placeholder;

      const selectedLabels = filteredOptions
        .filter((opt) => value.includes(opt.value))
        .map((opt) => opt.label);

      if (formatDisplayText) {
        return formatDisplayText({
          value,
          placeholder: placeholder ?? "",
          selectionMode: selectionMode === "single" ? "single" : "multiple",
          selectedLabels,
        });
      }

      if (selectionMode === "single") {
        const selectedOption = filteredOptions.find((opt) =>
          value.includes(opt.value)
        );
        return selectedOption?.label || placeholder;
      }

      if (showCount) {
        return `${value.length} ${t("common.selected")}`;
      }

      return selectedLabels.join(", ");
    };

    return (
      <div className="w-full">
        {label && (
          <Label
            htmlFor={selectId}
            className={cn(
              "mb-3 flex items-center gap-2 text-sm font-bold text-grey-900",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            <span>{label}</span>
            {labelIcon && <span>{labelIcon}</span>}
          </Label>
        )}

        <SelectPrimitive.Root
          value="__multiselect__"
          open={isOpen}
          onOpenChange={setIsOpen}
          disabled={disabled}
        >
          <div className="relative">
            <SelectPrimitive.Trigger
              id={selectId}
              ref={ref}
              aria-invalid={hasError}
              aria-describedby={hint || error ? hintId : undefined}
              className={cn(
                "peer flex rtl:flex-row-reverse w-full items-center justify-between gap-2 whitespace-nowrap rounded-lg border bg-white py-4 pe-10 rtl:pe-5 rtl:ps-10 text-sm font-semibold transition-colors",

                leftIcon
                  ? "ps-12 ltr:pe-5 rtl:ps-5 rtl:pe-12"
                  : "px-5 rtl:pe-5",

                darkModeOnOpen &&
                  isOpen &&
                  "bg-grey-900 text-white border-grey-900",

                !(darkModeOnOpen && isOpen) && "border-grey-100",
                !(darkModeOnOpen && isOpen) && !hasError && "text-grey-000",
                !(darkModeOnOpen && isOpen) && hasError && "text-error-700",
                !(darkModeOnOpen && isOpen) &&
                  !hasError &&
                  value.length === 0 &&
                  "text-grey-200",
                !(darkModeOnOpen && isOpen) &&
                  !hasError &&
                  value.length > 0 &&
                  "text-grey-900 border-grey-900",

                "hover:border-brand-green",
                "focus-visible:outline-none focus-visible:text-grey-900",
                "disabled:cursor-not-allowed disabled:bg-grey-50 disabled:text-grey-200",
                "aria-[invalid=true]:border-error-700 aria-[invalid=true]:bg-error-bg aria-[invalid=true]:text-error-700",
                className
              )}
            >
              <span className="flex items-center gap-1">
                {innerLabel && (
                  <span
                    className={cn(
                      "font-medium",
                      darkModeOnOpen && isOpen ? "text-white" : "text-grey-500"
                    )}
                  >
                    {innerLabel}:
                  </span>
                )}
                <span>{getDisplayText()}</span>
              </span>

              <SelectPrimitive.Icon asChild>
                <ChevronDown
                  className={cn(
                    "h-4 w-4",
                    darkModeOnOpen && isOpen ? "text-white" : "text-current"
                  )}
                />
              </SelectPrimitive.Icon>
            </SelectPrimitive.Trigger>

            {leftIcon && (
              <div
                className={cn(
                  "absolute start-5 top-1/2 -translate-y-1/2 pointer-events-none z-10 transition-colors",
                  darkModeOnOpen && isOpen && "text-white",
                  !(darkModeOnOpen && isOpen) && !hasError && "text-grey-900",
                  !(darkModeOnOpen && isOpen) && hasError && "text-error-700",
                  !(darkModeOnOpen && isOpen) &&
                    !hasError &&
                    value.length === 0 &&
                    "text-grey-200",
                  !(darkModeOnOpen && isOpen) &&
                    !hasError &&
                    value.length > 0 &&
                    "text-grey-900",
                  "peer-disabled:text-grey-200",
                  "peer-aria-[invalid=true]:text-error-700"
                )}
              >
                {leftIcon}
              </div>
            )}
          </div>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              position="popper"
              className={cn(
                "relative z-50 overflow-hidden rounded-md border bg-white text-grey-900 shadow-md",
                "min-w-[var(--radix-select-trigger-width)]",
                contentClassName
              )}
              onCloseAutoFocus={(e) => {
                e.preventDefault();
              }}
            >
              <SelectPrimitive.Viewport
                className={cn(
                  "max-h-[300px] overflow-y-auto p-0",
                  viewportClassName
                )}
              >
                {searchable && (
                  <div className="px-3 py-2">
                    <SearchInput
                      variant="input-only"
                      size="sm"
                      value={searchTerm}
                      placeholder={searchPlaceholder ?? t("common.search")}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      showButton={false}
                      className="border-0 shadow-none"
                    />
                   </div>
                )}
                {hasOptions ? (
                  <div
                    className={cn(
                      "flex gap-0",
                      selectionMode === "single"
                        ? "flex-col"
                        : checkboxVariant === "advanced"
                        ? "flex-wrap"
                        : "flex-col"
                    )}
                  >
                    {selectionMode === "single" ? (
                      <div className="flex flex-col gap-0">
                        {filteredOptions.map((option) => {
                          const isChecked = value.includes(option.value);
                          return (
                            <div
                              key={option.value}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleRadioChange(option.value, !isChecked);
                              }}
                              className={cn("cursor-pointer hover:bg-grey-50 py-3 px-4",checkboxClassName)}
                            >
                              <RadioButton
                                className="!items-center"
                                checked={isChecked}
                                onCheckedChange={(checked) =>
                                  handleRadioChange(option.value, checked)
                                }
                                label={option.label}
                                disabled={disabled}
                                value={option.value}
                                name={selectId}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      filteredOptions.map((option) => (
                        <div
                          key={option.value}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const isChecked = value.includes(option.value);
                            handleCheckboxChange(option.value, !isChecked);
                          }}
                          className="cursor-pointer"
                        >
                          <Checkbox
                            className={cn("!items-center hover:bg-grey-50 py-2 px-4",checkboxClassName)}
                            variant={checkboxVariant}
                            checked={value.includes(option.value)}
                            onCheckedChange={(checked) =>
                              handleCheckboxChange(option.value, checked)
                            }
                            label={option.label}
                            disabled={disabled}
                          />
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="px-2 py-1.5 text-sm text-grey-400">
                    {t("common.noOption")}
                  </div>
                )}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>

        {(hint || error) && (
          <p
            id={hint ? hintId : undefined}
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

MultiSelectBase.displayName = "MultiSelectBase";

const MultiSelect = React.forwardRef<
  HTMLButtonElement,
  MultiSelectProps<FieldValues, FieldPath<FieldValues>>
>((props, ref) => {
  const { name, control, rules, error: errorProp, ...restProps } = props;

  if (control && name) {
    return (
      <Controller
        name={name as any}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => (
          <MultiSelectBase
            {...restProps}
            value={field.value ?? []}
            onChange={field.onChange}
            error={fieldState.error?.message || errorProp}
            ref={ref}
          />
        )}
      />
    );
  }

  return <MultiSelectBase {...restProps} error={errorProp} ref={ref} />;
});

MultiSelect.displayName = "MultiSelect";

export { MultiSelect };
